<template>
  <teleport to="body">
    <div
      class="modal fade"
      id="assign_questions_modal"
      tabindex="-1"
      aria-labelledby="assign_questions_modal_label"
      aria-hidden="true"
      ref="modalElement"
      data-bs-backdrop="static"
    >
      <div class="modal-dialog modal-dialog-centered modal-lg">
        <div class="modal-content bg-white rounded-3 shadow-lg">
          <!-- Header -->
          <div class="modal-header border-bottom-0 cute-header sticky-top" style="z-index: 1;">
            <p class="modal-title text-white text-xxs mb-0" id="assign_questions_modal_label">
              Assign Questions to Condition
            </p>
            <button
              type="button"
              class="modal-btn-close btn-close btn-close-white"
              data-bs-dismiss="modal"
              aria-label="Close"
            ></button>
          </div>
          
          <!-- Body -->
          <div class="modal-body py-3">
            <div class="card mb-4 border-0">
              <div class="card-body p-0">
                <!-- Loader -->
                <div v-if="is_loading" class="text-center py-4">
                  <div class="spinner-border spinner-border-sm" role="status"></div>
                  <p class="text-muted mt-2 mb-0 fs-6">Loading questions...</p>
                </div>
                
                <div v-else>
                  <!-- Search -->
                  <div class="d-flex justify-content-between align-items-center mb-3">
                    <p class="text-muted mb-0 text-xs">
                      Toggle to assign or unassign <strong>main questions</strong>.
                      Dependent questions are assigned automatically.
                    </p>
                    <div class="input-group w-auto text-xxs">
                      <input
                        type="text"
                        class="form-control form-control-sm text-xxs rounded-lg"
                        placeholder="Search questions..."
                        v-model="question_search"
                        @keyup.esc="closeModal"
                      />
                    </div>
                  </div>
                  
                  <!-- Table Container with Fixed Height -->
                  <div class="table-container" style="max-height: 50vh; overflow-y: auto;">
                    <table class="table table-bordered table-hover align-middle mb-0">
                      <thead class="table-header text-white sticky-top" style="top: 0;">
                        <tr class="text-xs">
                          <th class="text-center">Question</th>
                          <th class="text-center">Type</th>
                          <th class="text-center">Number</th>
                          <th class="text-center">Assign</th>
                          <th v-if="hasSelectQuestions" class="text-center">Condition Options</th>
                        </tr>
                      </thead>
                      <tbody>
                        <!-- MAIN QUESTIONS -->
                        <template v-for="q in filtered_questions" :key="q.id">
                          <tr class="text-xxs">
                            <!-- Question column -->
                            <td>
                              <div class="d-flex align-items-start gap-2">
                                <span class="status-badge btn-primary-dark text-xxs rounded-lg">
                                  Main
                                </span>
                                <div style="max-width: 300px;">
                                  <div class="fw-medium text-truncate" :title="q.question_text">
                                    {{ q.question_text || 'No question text' }}
                                  </div>
                                  <small class="text-muted d-block">{{ q.form_name || '-' }}</small>
                                </div>
                              </div>
                            </td>
                            <!-- Type -->
                            <td class="text-center">
                              <span class="badge bg-light text-dark">
                                {{ formatQuestionType(q.question_type) }}
                              </span>
                            </td>
                            <!-- Number -->
                            <td class="text-center">{{ q.question_number || '-' }}</td>
                            <!-- Assign -->
                            <td class="text-center">
                              <div class="form-check form-switch d-flex justify-content-center">
                                <input
                                  class="form-check-input"
                                  type="checkbox"
                                  :checked="q.is_assigned"
                                  :disabled="q.is_loading"
                                  @change="onToggleAssign(q)"
                                  :class="{ 'is-invalid': q.has_error }"
                                />
                                <span v-if="q.is_loading" class="spinner-border spinner-border-sm ms-2"></span>
                              </div>
                              <small v-if="q.error_message" class="text-danger d-block">{{ q.error_message }}</small>
                            </td>
                            <!-- Condition Options for select questions -->
                            <td v-if="hasSelectQuestions" class="text-center">
                              <div v-if="isSelectQuestion(q.question_type)">
                                <!-- Show options when question is assigned OR in pending changes -->
                                <div v-if="q.is_assigned || isQuestionInPendingChanges(q)">
                                  <!-- Single Select -->
                                  <div v-if="isSingleSelect(q.question_type)">
                                    <select 
                                      class="form-select form-select-sm text-xxs"
                                      v-model="q.selected_option_id"
                                      :disabled="!q.is_assigned || q.is_loading"
                                      @change="onOptionSelectionChange(q)"
                                      :class="{ 'is-invalid': !q.selected_option_id && q.is_assigned }"
                                    >
                                      <option value="">Select option for condition</option>
                                      <option 
                                        v-for="option in q.options" 
                                        :key="option.id" 
                                        :value="option.id"
                                      >
                                        {{ option.label }}
                                      </option>
                                    </select>
                                    <small v-if="!q.selected_option_id && q.is_assigned" class="text-warning">
                                      Select an option to complete assignment
                                    </small>
                                  </div>
                                  
                                  <!-- Multi Select -->
                                  <div v-if="isMultiSelect(q.question_type)">
                                    <div class="options-container">
                                      <div 
                                        v-for="option in q.options" 
                                        :key="option.id"
                                        class="form-check form-check-inline"
                                      >
                                        <input
                                          class="form-check-input"
                                          type="checkbox"
                                          :id="`option-${q.id}-${option.id}`"
                                          :value="option.id"
                                          v-model="q.selected_option_ids"
                                          :disabled="!q.is_assigned || q.is_loading"
                                          @change="onOptionSelectionChange(q)"
                                        />
                                        <label 
                                          class="form-check-label text-xxs" 
                                          :for="`option-${q.id}-${option.id}`"
                                        >
                                          {{ option.label }}
                                        </label>
                                      </div>
                                    </div>
                                    <small v-if="q.selected_option_ids?.length" class="text-muted">
                                      {{ q.selected_option_ids.length }} option(s) selected
                                    </small>
                                    <small v-if="(!q.selected_option_ids || q.selected_option_ids.length === 0) && q.is_assigned" class="text-warning">
                                      Select at least one option
                                    </small>
                                  </div>
                                </div>
                                <span v-else class="text-muted">
                                  Toggle switch to select options
                                </span>
                              </div>
                              <span v-else class="text-muted">N/A</span>
                            </td>
                          </tr>

                          <!-- DEPENDENT QUESTIONS -->
                          <tr
                            v-for="dq in q.dependent_questions"
                            :key="'dep-' + dq.id"
                            class="bg-light text-xxs"
                          >
                            <td class="ps-4">
                              <div class="d-flex align-items-start gap-2">
                                <span class="status-badge bg-warning text-dark text-xxs rounded-lg">
                                  Dependent
                                </span>
                                <div style="max-width: 280px;">
                                  <div class="fw-medium text-truncate" :title="dq.question_text">
                                    {{ dq.question_text }}
                                  </div>
                                </div>
                              </div>
                            </td>

                            <td class="text-center">
                              <span class="badge bg-light text-dark">
                                {{ formatQuestionType(dq.question_type) }}
                              </span>
                            </td>
                            <td class="text-center">{{ dq.question_number || '-' }}</td>
                            <td class="text-center">
                              <span class="status-badge bg-info rounded-lg text-xxs">
                                Auto-assigned
                              </span>
                            </td>
                            <td v-if="hasSelectQuestions" class="text-center">
                              <span class="text-muted">N/A</span>
                            </td>
                          </tr>
                        </template>

                        <!-- Empty state -->
                        <tr v-if="filtered_questions.length === 0">
                          <td :colspan="hasSelectQuestions ? 5 : 4" class="text-center py-3 text-muted fs-6">
                            {{ question_search ? 'No matching questions found' : 'No questions available' }}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                <div class="d-flex justify-content-between align-items-center mt-3">
                <p class="text-muted text-xs mb-0">
                    Showing {{ filtered_questions.length }} of {{ questions.length }} questions
                </p>
                <button 
                    v-if="hasPendingChanges"
                    type="button" 
                    class="btn btn-primary btn-sm"
                    @click="saveAssignments"
                    :disabled="hasIncompleteSelectAssignments"
                >
                    Save Changes ({{ pending_changes.length }})
                </button>
                </div>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Footer -->
          <div class="modal-footer border-top py-2">
            <button 
              type="button" 
              class="btn btn-secondary btn-sm" 
              data-bs-dismiss="modal"
              @click="attemptClose"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  </teleport>
</template>